import { Card, CardContent, CardHeader, Typography, Grid2 as Grid, Stack, alpha } from '@mui/material';
import { GridRenderCellParams } from '@mui/x-data-grid-premium';
import React, { useMemo } from 'react';
import { ListChildComponentProps } from 'react-window';
import { useTranslation } from 'react-i18next';
import { usePath } from 'hookrouter';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { getActionsMenuProps, getColumnCellRenderer, getValueFormatter } from '../../utils/MUIDataGridUtils';
import { ActionsMenu } from '../ActionsMenu';
import { MUIDataGrid, MUIDataGridColumn } from '../../constants/MUIDataGrid';
import { AppRoutes } from '../../constants/AppRoutes';
import { unknownGroup } from '../../constants/Group';
import { I18nKeys } from '../../constants/I18nKeys';

export const UsersCard: React.FC<ListChildComponentProps> = ({ style, data: users, index }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const user = users[index];

  const path = usePath();
  const { availableDealers: dealers = [] } = useAppSelector((state) => state.currentUser);
  const { group: { configurators = [] } = unknownGroup } = useAppSelector((state) => state.group);

  const table = useMemo(() => (path.startsWith(AppRoutes.Team) ? MUIDataGrid.Users : MUIDataGrid.AllUsers), [path]);
  const [DealersCell, ConfiguratorsCell] = useMemo(
    () =>
      [MUIDataGridColumn.Configurators, MUIDataGridColumn.Dealers].map((column) =>
        getColumnCellRenderer(
          {
            table,
            dealers,
            configurators,
            showGroupMemberMenu: false,
            editable: false,
          },
          column,
          dispatch,
          t,
        )?.renderCell?.({
          row: user,
          value: user[column],
        } as GridRenderCellParams),
      ),
    [table, user, dealers, configurators, t, dispatch],
  );
  const permissions = useMemo(
    () =>
      getValueFormatter(
        {
          table,
          dealers,
          configurators,
          showGroupMemberMenu: false,
          editable: false,
        },
        MUIDataGridColumn.Permissions,
        t,
      )(user[MUIDataGridColumn.Permissions]),
    [table, dealers, configurators, t, user],
  );

  return (
    <Card
      elevation={0}
      key={user.username}
      style={style}
      sx={{
        p: '16px',
        borderRadius: '8px',
        height: '188px !important',
        ml: '24px',
        width: 'calc(100% - 48px) !important',
        backgroundColor: path.startsWith(AppRoutes.Groups) ? alpha('#F2F4F5', 0.8) : undefined,
      }}
    >
      <Stack direction="column" spacing="8px">
        <CardHeader
          title={
            <Typography variant="body1" fontWeight={600}>
              {user.name || user.email}
            </Typography>
          }
          sx={{ height: '24px', p: '0px', '& button': { position: 'absolute', top: '2px', right: '2px' } }}
          action={
            <ActionsMenu
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getActionsMenuProps(
                { table, dealers, configurators, showGroupMemberMenu: true, editable: false },
                user,
                dispatch,
                t,
              )}
            />
          }
        />
        <CardContent sx={{ p: '0px !important' }}>
          <Stack direction="column" spacing="8px">
            {user.name && user.email && (
              <Grid container>
                <Grid size={12}>
                  <Typography color="textSecondary" variant="body2">
                    {user.email}
                  </Typography>
                </Grid>
              </Grid>
            )}

            {user.phone && (
              <Grid container>
                <Grid size={12}>
                  <Typography color="textSecondary" variant="body2">
                    {user.phone}
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Grid container>
              <Grid size={4}>
                <Stack direction="column" spacing="8px">
                  <Typography variant="body2" fontWeight={600}>
                    {t(I18nKeys.TableHeaderConfigurators)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {ConfiguratorsCell}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={4}>
                <Stack direction="column" spacing="8px">
                  <Typography variant="body2" fontWeight={600}>
                    {t(I18nKeys.TableHeaderDealers)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {DealersCell}
                  </Typography>
                </Stack>
              </Grid>

              <Grid size={4}>
                <Stack direction="column" spacing="8px">
                  <Typography variant="body2" fontWeight={600}>
                    {t(I18nKeys.TableHeaderPermissions)}
                  </Typography>
                  <Typography color="textSecondary" variant="body2">
                    {permissions}
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </CardContent>
      </Stack>
    </Card>
  );
};
